import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Droplets,
  Zap,
  Leaf,
  Wallet,
  CheckCircle2,
  PhoneCall,
  ShieldCheck,
  Award,
  TrendingUp,
  Clock,
  Users,
  ArrowRight,
  Calculator,
  ThumbsUp,
} from 'lucide-react';
import { LOGO_URL } from '../data/mock';
import { LEGAL_ENTITY } from '../components/Footer';
import { ExpertBadge } from '../components/ExpertBadge';

// Animated counter hook (mobile-friendly, runs once)
const useCountUp = (target, duration = 1600, start = true) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
};

const HEAT_PUMP_HERO_IMG = `${process.env.PUBLIC_URL || ''}/images/istore-heatpump.jpg`;

const HeatPumpHotWaterPage = () => {
  const [animateNumbers, setAnimateNumbers] = useState(false);

  useEffect(() => {
    // Kick off counters after first paint
    const t = setTimeout(() => setAnimateNumbers(true), 200);
    return () => clearTimeout(t);
  }, []);

  // Indicative figures (with compliant hedging in copy)
  const fiveYearSavings = useCountUp(2000, 1800, animateNumbers); // ~$400/yr × 5
  const rebateAmount = useCountUp(1400, 1800, animateNumbers); // up to $1,400 locally made
  const combinedSavings = useCountUp(2030, 1800, animateNumbers); // up to $2,030 combined

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="hphw-landing-page">
      {/* ============ Slim Header ============ */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" data-testid="hphw-logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CompareYourOptions.com.au" className="h-8 w-auto" />
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-cyo-orange bg-orange-50 border border-orange-100 px-3 py-1 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5" />
            100% Free • No Obligation
          </span>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(120% 100% at 0% 0%, #FFF4E6 0%, #FFFFFF 55%, #E8F8F5 100%)',
        }}
        data-testid="hphw-hero"
      >
        {/* decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-orange-200/40 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 w-80 h-80 bg-emerald-200/40 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 pt-8 pb-12 sm:pt-12 sm:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* LEFT: copy + savings card */}
            <div className="lg:col-span-7">
              {/* Greeting badge */}
              <div className="inline-flex items-center gap-2 bg-white border border-orange-200 shadow-sm px-3 py-1.5 rounded-full">
                <Sparkles className="w-4 h-4 text-cyo-orange" />
                <span className="text-xs font-bold tracking-wide text-gray-800">
                  Thanks for enquiring — great choice!
                </span>
              </div>

              {/* Headline */}
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
                Your hot water bill
                <br />
                <span className="text-cyo-orange">could be a lot smaller</span>
                <br />
                this time next year.
              </h1>

              <p className="mt-5 text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed">
                You&apos;ve just taken the first step toward modern, energy-efficient hot
                water. While you wait for our consultant&apos;s call, here&apos;s a quick look at
                why so many Australian households are making the switch.
              </p>

              {/* Big savings card */}
              <div className="mt-7 relative">
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-3xl p-6 sm:p-8 shadow-2xl overflow-hidden">
                  <div className="pointer-events-none absolute -top-10 -right-10 w-44 h-44 bg-cyo-orange/20 rounded-full blur-2xl" />
                  <div className="pointer-events-none absolute -bottom-8 -left-8 w-40 h-40 bg-emerald-400/20 rounded-full blur-2xl" />

                  <div className="relative">
                    <p className="text-xs font-bold uppercase tracking-widest text-orange-300">
                      Potential 5-year savings
                    </p>
                    <div className="flex items-end gap-1 mt-1">
                      <span className="text-5xl sm:text-6xl font-black tabular-nums leading-none">
                        ${fiveYearSavings.toLocaleString()}
                      </span>
                      <span className="text-3xl sm:text-4xl font-black text-orange-300 pb-1 ml-1 leading-none">
                        +
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-gray-300 max-w-md">
                      Switching from gas to an efficient electric heat pump hot water
                      system <span className="font-semibold text-white">could save eligible households around $400 a year</span> on
                      energy bills*. Over 5 years, that adds up.
                    </p>

                    <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4">
                      <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-3 sm:p-4">
                        <p className="text-[10px] sm:text-xs uppercase tracking-wider text-orange-200 font-bold">
                          Government rebate
                        </p>
                        <p className="mt-1 text-2xl sm:text-3xl font-black tabular-nums">
                          up to ${rebateAmount.toLocaleString()}
                        </p>
                        <p className="text-[11px] sm:text-xs text-gray-300 mt-0.5">
                          VIC Solar Homes (eligible locally-made)*
                        </p>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-3 sm:p-4">
                        <p className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-200 font-bold">
                          Combined incentives
                        </p>
                        <p className="mt-1 text-2xl sm:text-3xl font-black tabular-nums">
                          up to ${combinedSavings.toLocaleString()}
                        </p>
                        <p className="text-[11px] sm:text-xs text-gray-300 mt-0.5">
                          Rebate + Victorian Energy Upgrades*
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
                  *Indicative figures based on Solar Victoria&apos;s Solar Homes Program (current
                  as at 2025). Actual savings &amp; rebates depend on your usage, household
                  eligibility, installer pricing and chosen product.
                </p>
              </div>

              {/* Soft CTA — what's next */}
              <div className="mt-7 flex items-center gap-3 bg-white/80 backdrop-blur border border-orange-100 rounded-2xl p-4 shadow-sm">
                <div className="w-11 h-11 rounded-full bg-cyo-orange/10 flex items-center justify-center flex-shrink-0">
                  <PhoneCall className="w-5 h-5 text-cyo-orange" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-900">
                    A consultant will call you shortly
                  </p>
                  <p className="text-xs text-gray-600">
                    We&apos;ll walk you through your options, rebates &amp; expected savings — no pressure.
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT: hero visual — hidden on mobile (saves vertical space) */}
            <div className="hidden lg:block lg:col-span-5">
              <div className="relative">
                {/* main image */}
                <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl border border-white/60">
                  <img
                    src={HEAT_PUMP_HERO_IMG}
                    alt="iStore heat pump hot water system installed at an Australian home"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  {/* gradient sheen */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  {/* badge inside image */}
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full shadow-md">
                    <Zap className="w-3.5 h-3.5 text-cyo-orange" />
                    <span className="text-[11px] font-bold text-gray-900">
                      Up to 4× more efficient
                    </span>
                  </div>
                </div>

                {/* Floating savings chip */}
                <div className="absolute -bottom-5 -left-6 bg-white shadow-xl border border-gray-100 rounded-2xl px-4 py-3 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                      Avg. yearly savings
                    </p>
                    <p className="text-lg font-black text-gray-900 tabular-nums leading-none">
                      ~$400/yr*
                    </p>
                  </div>
                </div>

                {/* Floating rebate chip */}
                <div className="absolute -top-4 -right-4 bg-cyo-orange text-white shadow-xl rounded-2xl px-4 py-3 flex items-center gap-3 rotate-2">
                  <Award className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-orange-100">
                      Rebate may apply
                    </p>
                    <p className="text-base font-black leading-none">up to $1,400</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY YOU MADE A SMART CHOICE ============ */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16" data-testid="hphw-why">
        <div className="flex justify-center mb-8">
          <ExpertBadge variant="light" />
        </div>
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
            <ThumbsUp className="w-3.5 h-3.5" />
            You made a smart call
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
            Why thousands of Aussie homes
            <br className="hidden sm:block" /> are <span className="text-cyo-orange">switching to heat pumps</span>
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            They&apos;re modern, efficient, and often eligible for rebates. Here&apos;s what
            people love about them.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {[
            {
              icon: Wallet,
              title: 'Potentially lower running costs',
              body:
                'Heat pumps use up to 4× less energy than traditional electric storage. May reduce hot water energy use significantly depending on usage.',
              tone: 'orange',
              testid: 'why-card-running-costs',
            },
            {
              icon: Award,
              title: 'Rebates may apply',
              body:
                'Eligible Victorian households may receive up to $1,000 (or up to $1,400 for eligible locally-made systems) under the Solar Homes hot water rebate.',
              tone: 'emerald',
              testid: 'why-card-rebates',
            },
            {
              icon: Leaf,
              title: 'Cleaner, future-ready energy',
              body:
                'Pairs beautifully with solar. Cuts carbon emissions from heating water — a meaningful step toward an all-electric home.',
              tone: 'orange',
              testid: 'why-card-clean-energy',
            },
            {
              icon: TrendingUp,
              title: 'Growing in popularity',
              body:
                'Hot water is around 1 in every 4 dollars of an average home energy bill. Switching is now one of the most popular electrification upgrades in Australia.',
              tone: 'emerald',
              testid: 'why-card-popularity',
            },
          ].map(({ icon: Icon, title, body, tone, testid }) => (
            <div
              key={title}
              data-testid={testid}
              className="group relative bg-white border border-gray-200 hover:border-cyo-orange/40 hover:shadow-lg transition-all rounded-2xl p-5 sm:p-6"
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  tone === 'orange'
                    ? 'bg-orange-50 text-cyo-orange'
                    : 'bg-emerald-50 text-emerald-600'
                }`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 leading-snug">{title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ 5-YEAR SAVINGS VISUAL ============ */}
      <section
        className="relative bg-gradient-to-b from-orange-50/60 via-white to-emerald-50/40 py-14 sm:py-20"
        data-testid="hphw-five-year"
      >
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-cyo-orange/10 border border-cyo-orange/20 text-cyo-orange px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
              <Calculator className="w-3.5 h-3.5" />
              Indicative 5-year picture
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
              How <span className="text-cyo-orange">potential savings</span> add up
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
              The chart below is a simple illustration of how yearly savings can
              accumulate over five years — actual amounts will vary with your
              usage, household size and tariff.
            </p>
          </div>

          {/* Bar chart visual */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 sm:p-8">
            <div className="grid grid-cols-5 gap-2 sm:gap-4 h-64 sm:h-80">
              {[1, 2, 3, 4, 5].map((year) => {
                const total = year * 400;
                const heightPct = (year / 5) * 100;
                return (
                  <div
                    key={year}
                    className="flex flex-col items-center"
                    data-testid={`bar-year-${year}`}
                  >
                    <span className="text-xs sm:text-sm font-bold tabular-nums text-gray-700 mb-2">
                      ${total.toLocaleString()}
                    </span>
                    <div className="relative w-full flex-1 min-h-0">
                      <div
                        className="absolute bottom-0 left-0 right-0 rounded-t-xl shadow-md"
                        style={{
                          height: animateNumbers ? `${heightPct}%` : '0%',
                          background:
                            year === 5
                              ? 'linear-gradient(180deg, #F7941D 0%, #E07D0A 100%)'
                              : 'linear-gradient(180deg, #FBBF77 0%, #F7941D 100%)',
                          transition: 'height 1.2s cubic-bezier(0.16,1,0.3,1)',
                          transitionDelay: `${year * 90}ms`,
                        }}
                      />
                    </div>
                    <span className="text-[10px] sm:text-xs font-semibold text-gray-500 mt-2">
                      Year {year}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="bg-orange-50 border border-orange-100 rounded-xl py-3 px-2">
                <p className="text-[10px] sm:text-xs uppercase tracking-wider text-cyo-orange font-bold">
                  Yearly potential
                </p>
                <p className="text-lg sm:text-xl font-black text-gray-900 tabular-nums mt-0.5">
                  ~$400
                </p>
              </div>
              <div className="bg-gray-900 text-white rounded-xl py-3 px-2">
                <p className="text-[10px] sm:text-xs uppercase tracking-wider text-orange-300 font-bold">
                  Over 5 years
                </p>
                <p className="text-lg sm:text-xl font-black tabular-nums mt-0.5">
                  ~$2,000+
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl py-3 px-2">
                <p className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-700 font-bold">
                  Plus rebate
                </p>
                <p className="text-lg sm:text-xl font-black text-gray-900 tabular-nums mt-0.5">
                  up to $1,400
                </p>
              </div>
            </div>

            <p className="mt-4 text-[11px] text-gray-500 leading-relaxed text-center">
              Based on Solar Victoria&apos;s estimate that switching from gas to an
              efficient electric hot water system could save households around $400 a
              year. Your actual result depends on usage and eligibility.
            </p>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS — Heat Pump Simply Explained ============ */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16" data-testid="hphw-how">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
              <Droplets className="w-3.5 h-3.5" />
              How it works (simply)
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
              Think of it as a <span className="text-cyo-orange">fridge in reverse</span>
            </h2>
            <p className="mt-3 text-gray-600 leading-relaxed">
              Instead of generating heat with an element, a heat pump <strong>moves heat
              from the outside air into your water tank</strong>. That&apos;s why it can be
              dramatically more efficient than traditional electric systems.
            </p>

            <ul className="mt-5 space-y-3">
              {[
                'Pulls free warmth from the surrounding air',
                'Uses electricity only to move heat (not create it)',
                'Quietly heats a stored tank of hot water for whenever you need it',
                'Works year-round — even on cool Aussie mornings',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="aspect-[4/5] sm:aspect-[4/4] w-full rounded-3xl overflow-hidden shadow-xl border border-gray-100 relative">
              <img
                src={HEAT_PUMP_HERO_IMG}
                alt="iStore heat pump hot water system installed at an Australian home"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                <span className="inline-flex items-center gap-1.5 bg-white/95 text-gray-900 text-xs font-bold px-2.5 py-1 rounded-full">
                  <Zap className="w-3.5 h-3.5 text-cyo-orange" />
                  Up to 4× more efficient than electric storage
                </span>
              </div>
            </div>
            {/* Floating chip */}
            <div className="hidden sm:flex absolute -bottom-5 -left-5 items-center gap-2 bg-white shadow-xl border border-gray-100 rounded-2xl px-3 py-2">
              <div className="w-9 h-9 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
                <Leaf className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                  Cleaner energy
                </p>
                <p className="text-xs font-bold text-gray-900">Pairs great with solar</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ REBATE SPOTLIGHT ============ */}
      <section
        className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-orange-900/80 text-white py-14 sm:py-20"
        data-testid="hphw-rebates"
      >
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-cyo-orange/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-orange-200">
              <Award className="w-3.5 h-3.5" />
              Government incentives
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
              Rebates may <span className="text-cyo-orange">significantly reduce</span>
              <br className="hidden sm:block" /> what you pay upfront
            </h2>
            <p className="mt-3 text-gray-300 max-w-2xl mx-auto text-sm sm:text-base">
              Eligible households may be able to combine Solar Victoria&apos;s rebate
              with Victorian Energy Upgrades discounts.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5" data-testid="rebate-card-solar-homes">
              <p className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">
                Solar Homes hot water rebate
              </p>
              <p className="mt-2 text-3xl font-black">up to $1,400</p>
              <p className="text-sm text-gray-300 mt-2">
                For eligible locally-made heat pump systems (from 1 July 2025).
                Otherwise up to $1,000.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5" data-testid="rebate-card-combined">
              <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
                Combined with VEU
              </p>
              <p className="mt-2 text-3xl font-black">up to $2,030</p>
              <p className="text-sm text-gray-300 mt-2">
                Combining the rebate with Victorian Energy Upgrades may reduce
                upfront cost by up to $2,030 for eligible locally-made systems.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5" data-testid="rebate-card-bill-savings">
              <p className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">
                Yearly bill savings
              </p>
              <p className="mt-2 text-3xl font-black">~$400 / yr</p>
              <p className="text-sm text-gray-300 mt-2">
                Solar Victoria estimate for switching from gas to an efficient
                electric hot water system.
              </p>
            </div>
          </div>

          <p className="mt-6 text-[11px] text-gray-400 leading-relaxed text-center max-w-3xl mx-auto">
            Source: Solar Victoria — Solar Homes Program / Hot Water Rebate. Eligibility
            and amounts depend on property, household and product. Our consultant will
            confirm what applies to you on your call.
          </p>
        </div>
      </section>

      {/* ============ WHAT HAPPENS NEXT ============ */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16" data-testid="hphw-next">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-100 text-cyo-orange px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
            <Clock className="w-3.5 h-3.5" />
            What happens next
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
            Look out for a call from your <span className="text-cyo-orange">Compare Your Options consultant</span>
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            Friendly, no-pressure, and built around your home — not a sales script.
          </p>
        </div>

        <div className="relative">
          {/* Connecting line on desktop (horizontal, behind icons) */}
          <div className="hidden md:block absolute left-[12.5%] right-[12.5%] top-7 h-0.5 bg-gradient-to-r from-cyo-orange via-orange-300 to-emerald-300 z-0" />
          {/* Connecting line on mobile (vertical) */}
          <div className="md:hidden absolute left-7 top-2 bottom-2 w-0.5 bg-gradient-to-b from-cyo-orange via-orange-300 to-emerald-300" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 md:gap-4">
            {[
              {
                icon: PhoneCall,
                title: 'A short, friendly call',
                body:
                  "We'll confirm a few quick details about your home and current hot water setup.",
              },
              {
                icon: Calculator,
                title: 'Tailored savings & rebate estimate',
                body:
                  'Your consultant maps out which rebates may apply and what your potential savings could look like.',
              },
              {
                icon: Users,
                title: 'Compare your best options',
                body:
                  "You'll see a clear comparison of suitable products and price ranges — no pressure to decide on the call.",
              },
              {
                icon: ShieldCheck,
                title: 'You stay in control',
                body:
                  'Take your time, ask questions, and only move forward if it feels right for your household.',
              },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`next-step-${i + 1}`}
                  className="relative flex md:flex-col items-start md:items-center gap-4 md:gap-0 md:text-center"
                >
                  {/* Icon circle */}
                  <div className="relative z-10 flex-shrink-0 w-14 h-14 rounded-full bg-white border-2 border-cyo-orange shadow-md flex items-center justify-center md:mb-4">
                    <Icon className="w-6 h-6 text-cyo-orange" />
                    {/* Step number badge */}
                    <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-cyo-orange text-white text-[11px] font-black flex items-center justify-center shadow-sm border-2 border-white">
                      {i + 1}
                    </span>
                  </div>

                  {/* Card */}
                  <div className="flex-1 bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow rounded-2xl p-4 md:p-5 w-full">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyo-orange">
                      Step {i + 1}
                    </p>
                    <h3 className="mt-1 text-base font-bold text-gray-900 leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Reassurance card */}
        <div className="mt-10 bg-gradient-to-r from-orange-50 via-white to-emerald-50 border border-orange-100 rounded-2xl p-5 sm:p-6 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-cyo-orange text-white flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm sm:text-base font-bold text-gray-900">
              No pressure. No surprises. No obligation.
            </p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              We&apos;re here to help you compare and decide on your terms. Your details
              are kept private and only used to assist with your enquiry.
            </p>
          </div>
        </div>
      </section>

      {/* ============ SOFT CTA ============ */}
      <section
        className="relative bg-gradient-to-br from-cyo-orange to-orange-600 text-white py-14 sm:py-20 overflow-hidden"
        data-testid="hphw-cta"
      >
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-10 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <Sparkles className="w-10 h-10 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Your call is on the way.
            <br />
            <span className="text-white">Looking forward to a great chat.</span>
          </h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto text-sm sm:text-base">
            In the meantime, feel free to explore other ways your home could save —
            from solar and batteries to EV chargers and heating &amp; cooling.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/compare"
              data-testid="hphw-cta-compare-link"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyo-orange font-bold px-6 py-3.5 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Explore more savings
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:"
              data-testid="hphw-cta-phone"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white font-bold px-6 py-3.5 rounded-full hover:bg-white/20 transition-all"
            >
              <PhoneCall className="w-4 h-4" />
              Wait for our call
            </a>
          </div>
        </div>
      </section>

      {/* ============ TRUST / FOOTER ============ */}
      <footer className="bg-gray-950 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Link to="/" className="inline-block">
            <img
              src={LOGO_URL}
              alt={LEGAL_ENTITY.website}
              className="h-8 w-auto mx-auto brightness-0 invert opacity-90"
            />
          </Link>
          <p className="mt-4 text-xs sm:text-sm">
            {LEGAL_ENTITY.website} is owned and operated by {LEGAL_ENTITY.name}{' '}
            (ABN {LEGAL_ENTITY.abn}).
          </p>
          <p className="mt-2 text-[11px] leading-relaxed max-w-2xl mx-auto text-gray-500">
            All savings and rebate figures shown are indicative only. Actual results
            vary based on your home, usage, hot water needs and government eligibility.
            Rebate references are sourced from Solar Victoria&apos;s Solar Homes Program
            (current as at 2025).
          </p>
          <div className="mt-4 flex justify-center gap-4 text-xs">
            <Link to="/privacy" className="hover:text-white">
              Privacy
            </Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-white">
              Terms
            </Link>
            <span>•</span>
            <Link to="/about" className="hover:text-white">
              About
            </Link>
          </div>
          <p className="mt-6 text-[11px] text-gray-600">
            © {new Date().getFullYear()} {LEGAL_ENTITY.website}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HeatPumpHotWaterPage;
